import React, { useState, useEffect, useMemo } from "react";
import api from "../../api";
import HrLayout from "../HrLayout";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  MenuItem,
  Paper,
  Grid,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Stack,
  TableContainer,
  TablePagination,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  AccountBalanceWallet,
  Paid,
  FileDownload as FileDownloadIcon,
  Save,
  CurrencyRupee,
  Payment,
  PictureAsPdf,
  Search,
  Delete as DeleteIcon,
  Assessment as ReportIcon,
} from "@mui/icons-material";

// Helper function to download CSV
const downloadCSV = (data, filename) => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]).join(",");
  const rows = data.map(row => 
    Object.values(row).map(val => 
      typeof val === "string" && val.includes(",") ? `"${val}"` : val
    ).join(",")
  );
  const csvContent = [headers, ...rows].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Reusable Enhanced Table
const EnhancedTable = ({ title, data, columns, emptyText = "No records found", onDelete }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    return data.filter(row =>
      Object.values(row).some(val =>
        val != null && val.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const csvData = filteredData.map(row =>
    columns.reduce((acc, col) => {
      acc[col.key] = col.format ? col.format(row[col.key], row) : (row[col.key] ?? "-");
      return acc;
    }, {})
  );

  return (
    <Box mt={5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Tooltip title="Download CSV">
            <IconButton
              color="primary"
              onClick={() => downloadCSV(csvData, title.replace(/[^a-z0-9]/gi, '_').toLowerCase())}
              disabled={filteredData.length === 0}
            >
              <FileDownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ bgcolor: "grey.100" }}>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key} sx={{ fontWeight: "bold" }}>
                  {col.label}
                </TableCell>
              ))}
              {onDelete && <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length + (onDelete ? 1 : 0)} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, idx) => (
                <TableRow key={idx} hover>
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      {col.format ? col.format(row[col.key], row) : row[col.key] ?? "-"}
                    </TableCell>
                  ))}
                  {onDelete && (
                    <TableCell>
                      <Tooltip title="Delete Structure">
                        <IconButton color="error" onClick={() => onDelete(row)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </Box>
  );
};

export default function SalaryModule() {
  const [tab, setTab] = useState(0);
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [loading, setLoading] = useState(false);
  const [calculatingAmount, setCalculatingAmount] = useState(false);

  const initialStructure = {
    basic: 0,
    hra: 0,
    conveyance: 0,
    special_allowance: 0,
    medical: 0,
    bonus: 0,
    pf: 0,
    esi: 0,
    professional_tax: 0,
    tds: 0,
  };

  const [structureForm, setStructureForm] = useState(initialStructure);

  const [payForm, setPayForm] = useState({
    employee_id: "",
    month: 12, // December 2025
    year: 2025,
    amount: "",
    payment_mode: "BANK",
    transaction_ref: "",
  });

  const [slipForm, setSlipForm] = useState({
    employee_id: "",
    month: 12,
    year: 2025,
  });

  const [reportForm, setReportForm] = useState({
    month: 12,
    year: 2025,
  });

  const [allStructures, setAllStructures] = useState([]);
  const [allPaymentTransactions, setAllPaymentTransactions] = useState([]);
  const [paymentSummaryData, setPaymentSummaryData] = useState([]);
  const [availableSlips, setAvailableSlips] = useState([]);
  const [salaryReport, setSalaryReport] = useState([]);
  const [slipUrl, setSlipUrl] = useState("");

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (tab === 0) fetchAllStructures();
    if (tab === 1) {
      fetchAllPaymentTransactions();     // ← Fixed: New endpoint
      fetchPaymentSummaryData();         // ← Fixed: Correct endpoint
    }
    if (tab === 2) fetchAvailableSlips();
    if (tab === 3) fetchSalaryReport();
  }, [tab]);

  useEffect(() => {
    if (payForm.employee_id && payForm.month && payForm.year) {
      fetchRemainingAmount();
    } else {
      setPayForm(prev => ({ ...prev, amount: "" }));
    }
  }, [payForm.employee_id, payForm.month, payForm.year]);

  useEffect(() => {
    if (tab === 3) fetchSalaryReport();
  }, [reportForm.month, reportForm.year]);

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/hr/employees");
      setEmployees(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllStructures = async () => {
    try {
      const res = await api.get("/salary/structures");
      setAllStructures(res.data || []);
    } catch (err) {
      setAllStructures([]);
    }
  };

  // FIXED: Now calling correct endpoint with all transactions + employee name
  const fetchAllPaymentTransactions = async () => {
    try {
      const res = await api.get("/salary/payments/all-history");
      setAllPaymentTransactions(res.data || []);
    } catch (err) {
      console.error("Error fetching all payment history:", err);
      setAllPaymentTransactions([]);
    }
  };

  // FIXED: Correct endpoint for total paid summary
  const fetchPaymentSummaryData = async () => {
    try {
      const res = await api.get(`/salary/payments/summary?month=${payForm.month}&year=${payForm.year}`);
      setPaymentSummaryData(res.data || []);
    } catch (err) {
      console.error("Error fetching payment summary:", err);
      setPaymentSummaryData([]);
    }
  };

  const fetchAvailableSlips = async () => {
    try {
      const res = await api.get("/salary/slips-list");
      setAvailableSlips(res.data || []);
    } catch (err) {
      setAvailableSlips([]);
    }
  };

  const fetchSalaryReport = async () => {
    try {
      const res = await api.get(`/salary/report?month=${reportForm.month}&year=${reportForm.year}`);
      setSalaryReport(res.data || []);
    } catch (err) {
      setSalaryReport([]);
    }
  };

const fetchRemainingAmount = async () => {
  setCalculatingAmount(true);
  try {
    const res = await api.get(
      `/salary/remaining-payable?employee_id=${payForm.employee_id}&month=${payForm.month}&year=${payForm.year}`
    );
    const remaining = res.data.remaining || 0;

    // Hamesha amount set karo — chahe 0 ho ya positive
    setPayForm(prev => ({ 
      ...prev, 
      amount: remaining >= 0 ? remaining.toString() : "" 
    }));

    // Sirf tab alert dikhao jab remaining 0 ho (full paid)
    if (remaining <= 0) {
      alert("Salary for this month/year is already fully paid or no structure defined.");
    }
  } catch (err) {
    console.error("Error fetching remaining payable:", err);
    setPayForm(prev => ({ ...prev, amount: "" }));
    // Optional: alert on error
    // alert("Could not calculate remaining salary. Please try again.");
  } finally {
    setCalculatingAmount(false);
  }
};
  const totalEarnings = ["basic", "hra", "conveyance", "special_allowance", "medical", "bonus"].reduce(
    (acc, key) => acc + Number(structureForm[key] || 0),
    0
  );

  const totalDeductions = ["pf", "esi", "professional_tax", "tds"].reduce(
    (acc, key) => acc + Number(structureForm[key] || 0),
    0
  );

  const netPay = totalEarnings - totalDeductions;

  const getPaymentSummary = () => {
    const summaryMap = {};

    allStructures.forEach((struct) => {
      const earnings = Number(struct.basic || 0) + Number(struct.hra || 0) + Number(struct.conveyance || 0) +
                       Number(struct.special_allowance || 0) + Number(struct.medical || 0) + Number(struct.bonus || 0);
      const deductions = Number(struct.pf || 0) + Number(struct.esi || 0) + Number(struct.professional_tax || 0) + Number(struct.tds || 0);
      const netSalary = earnings - deductions;

      summaryMap[struct.emp_code] = {
        employee_name: struct.employee_name,
        total_payable: netSalary,
        paid: 0,
      };
    });

    paymentSummaryData.forEach((item) => {
      if (summaryMap[item.employee_id]) {
        summaryMap[item.employee_id].paid = Number(item.total_paid || 0);
      }
    });

    return Object.values(summaryMap).map((item) => ({
      ...item,
      remaining: Math.max(0, item.total_payable - item.paid),
    }));
  };

  const paymentSummary = getPaymentSummary();

  const handleStructureEmployee = async (e, value) => {
    const empId = value?.id || "";
    setEmployeeId(empId);
    if (!empId) {
      setStructureForm(initialStructure);
      return;
    }
    try {
      const res = await api.get(`/salary/structure/${empId}`);
      setStructureForm(res.data || initialStructure);
    } catch (err) {
      setStructureForm(initialStructure);
    }
  };

  const handleStructureSave = async () => {
    if (!employeeId) return alert("Please select an employee first");
    setLoading(true);
    try {
      await api.post("/salary/structure", { employee_id: employeeId, ...structureForm });
      alert("Salary structure updated successfully");
      fetchAllStructures();
    } catch (err) {
      alert("Failed to save structure");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStructure = async (row) => {
    if (!window.confirm(`Delete salary structure for ${row.employee_name}?`)) return;
    try {
      await api.delete(`/salary/structure/${row.emp_code}`);
      alert("Structure deleted");
      fetchAllStructures();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const handlePaySalary = async () => {
    if (!payForm.employee_id || !payForm.amount || Number(payForm.amount) <= 0) {
      return alert("Invalid amount or employee not selected");
    }
    setLoading(true);
    try {
      await api.post("/salary/pay", payForm);
      alert("Payment successful");
      setPayForm(prev => ({ ...prev, amount: "", transaction_ref: "" }));
      fetchAllPaymentTransactions();
      fetchPaymentSummaryData();
    } catch (err) {
      alert("Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSlip = async () => {
    if (!slipForm.employee_id) {
      alert("Please select an employee");
      return;
    }
    setLoading(true);
    setSlipUrl("");
    try {
      const res = await api.get(`/salary/slip/${slipForm.employee_id}/${slipForm.month}/${slipForm.year}`);
      const baseUrl = api.defaults.baseURL.replace("/api", "");
      setSlipUrl(`${baseUrl}${res.data.url}`);
      fetchAvailableSlips();
    } catch (err) {
      alert("Failed to generate slip. Check if structure exists.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <HrLayout>
      <Box p={4} sx={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Typography variant="h4" fontWeight="800" color="primary.main">
            Payroll Center
          </Typography>
          <Chip icon={<CurrencyRupee />} label="Salary Management" color="secondary" variant="outlined" />
        </Stack>

        <Paper sx={{ mb: 4, borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="fullWidth">
            <Tab icon={<AccountBalanceWallet />} label="STRUCTURE" />
            <Tab icon={<Payment />} label="PAYMENTS" />
            <Tab icon={<PictureAsPdf />} label="SLIPS" />
            <Tab icon={<ReportIcon />} label="REPORT" />
          </Tabs>
        </Paper>

        {/* TAB 0: STRUCTURE */}
        {tab === 0 && (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Autocomplete
                  options={employees}
                  getOptionLabel={(o) => `${o.first_name} ${o.last_name} (${o.employee_id || o.id})`}
                  onChange={handleStructureEmployee}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Employee to configure Salary Structure" variant="outlined" fullWidth sx={{ bgcolor: "white", minWidth: 400 }} />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" color="success.main" mb={2} fontWeight="bold">Earnings</Typography>
                    {["basic", "hra", "conveyance", "special_allowance", "medical", "bonus"].map((key) => (
                      <TextField
                        key={key}
                        label={key.replace("_", " ").toUpperCase()}
                        size="small"
                        type="number"
                        fullWidth
                        margin="dense"
                        value={structureForm[key]}
                        onChange={(e) => setStructureForm({ ...structureForm, [key]: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, height: "100%" }}>
                  <CardContent>
                    <Typography variant="h6" color="error.main" mb={2} fontWeight="bold">Deductions</Typography>
                    {["pf", "esi", "professional_tax", "tds"].map((key) => (
                      <TextField
                        key={key}
                        label={key.replace("_", " ").toUpperCase()}
                        size="small"
                        type="number"
                        fullWidth
                        margin="dense"
                        value={structureForm[key]}
                        onChange={(e) => setStructureForm({ ...structureForm, [key]: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      />
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ borderRadius: 3, bgcolor: "primary.main", color: "white", p: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography variant="overline">Monthly Net Pay Estimate</Typography>
                  <Typography variant="h3">₹{netPay.toLocaleString()}</Typography>
                  <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.2)" }} />
                  <LoadingButton
                    loading={loading}
                    variant="contained"
                    fullWidth
                    onClick={handleStructureSave}
                    startIcon={<Save />}
                    disabled={!employeeId}
                    sx={{ bgcolor: "white", color: "primary.main", "&:hover": { bgcolor: "#f0f0f0" } }}
                  >
                    Save Structure
                  </LoadingButton>
                </Card>
              </Grid>
            </Grid>

            <EnhancedTable
              title="All Salary Structures"
              data={allStructures}
              onDelete={handleDeleteStructure}
              columns={[
                { key: "employee_name", label: "Employee" },
                { key: "basic", label: "Basic", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                { key: "hra", label: "HRA", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                { key: "conveyance", label: "Conveyance", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                { key: "special_allowance", label: "Special Allow.", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                { key: "medical", label: "Medical", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                { key: "bonus", label: "Bonus", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                {
                  key: "net_salary",
                  label: "Net Salary",
                  format: (v, row) => {
                    const e = ["basic","hra","conveyance","special_allowance","medical","bonus"].reduce((a,k)=>a+Number(row[k]||0),0);
                    const d = ["pf","esi","professional_tax","tds"].reduce((a,k)=>a+Number(row[k]||0),0);
                    return `₹${(e - d).toLocaleString()}`;
                  },
                },
              ]}
              emptyText="No salary structures created yet"
            />
          </>
        )}

        {/* TAB 1: PAYMENTS */}
        {tab === 1 && (
          <>
            <Box component={Paper} p={4} borderRadius={3} mb={4}>
              <Typography variant="h6" mb={3} fontWeight="bold">Salary Payment Entry</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={employees}
                    getOptionLabel={(o) => `${o.first_name} ${o.last_name}`}
                    onChange={(e, v) => setPayForm({ ...payForm, employee_id: v?.id || "", amount: "" })}
                    renderInput={(params) => <TextField {...params} label="Select Employee" sx={{ minWidth: 300 }} />}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select fullWidth label="Month" value={payForm.month} onChange={(e) => setPayForm({ ...payForm, month: e.target.value, amount: "" })}>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <MenuItem key={m} value={m}>{new Date(0, m - 1).toLocaleString("en", { month: "long" })}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Year" type="number" value={payForm.year} onChange={(e) => setPayForm({ ...payForm, year: e.target.value, amount: "" })} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Amount to Pay"
                    type="number"
                    value={payForm.amount}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                      readOnly: true,
                    }}
                    helperText={calculatingAmount ? "Calculating..." : "Auto-calculated remaining"}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField select fullWidth label="Payment Mode" value={payForm.payment_mode} onChange={(e) => setPayForm({ ...payForm, payment_mode: e.target.value })}>
                    <MenuItem value="BANK">Bank Transfer</MenuItem>
                    <MenuItem value="CASH">Cash</MenuItem>
                    <MenuItem value="UPI">UPI / Digital</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Transaction Ref / Cheque No." value={payForm.transaction_ref} onChange={(e) => setPayForm({ ...payForm, transaction_ref: e.target.value })} />
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    fullWidth
                    loading={loading}
                    variant="contained"
                    color="success"
                    size="large"
                    onClick={handlePaySalary}
                    startIcon={<Paid />}
                    disabled={!payForm.employee_id || !payForm.amount || Number(payForm.amount) <= 0}
                  >
                    Process Payment of ₹{payForm.amount ? Number(payForm.amount).toLocaleString() : "0"}
                  </LoadingButton>
                </Grid>
              </Grid>
            </Box>

            <EnhancedTable
              title="Payment History (All Transactions)"
              data={allPaymentTransactions}
              columns={[
                { key: "employee_name", label: "Employee" },
                { key: "amount", label: "Amount", format: (v) => `₹${Number(v || 0).toLocaleString()}` },
                { key: "payment_mode", label: "Mode" },
                { key: "transaction_ref", label: "Ref No.", format: v => v || "-" },
                { key: "created_at", label: "Paid On", format: v => v ? new Date(v).toLocaleDateString() : "-" },
                { key: "month", label: "Period", format: (_, r) => r.month && r.year ? `${r.month}/${r.year}` : "-" },
              ]}
              emptyText="No payments recorded yet"
            />

            <EnhancedTable
              title="Payment Summary (Current Month)"
              data={paymentSummary}
              columns={[
                { key: "employee_name", label: "Employee" },
                { key: "total_payable", label: "Total Payable", format: (v) => `₹${Number(v).toLocaleString()}` },
                { key: "paid", label: "Paid", format: (v) => `₹${Number(v).toLocaleString()}` },
                { key: "remaining", label: "Remaining", format: (v) => <Typography color={v > 0 ? "error" : "success"} fontWeight="bold">₹{v.toLocaleString()}</Typography> },
              ]}
              emptyText="No salary structures defined"
            />
          </>
        )}

        {/* TAB 2: SLIPS */}
       {tab === 2 && (
  <>
    {/* Generate Slip Section - Professional Card Style */}
    <Paper elevation={6} sx={{ p: 5, borderRadius: 4, mb: 6, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
      <Typography variant="h5" fontWeight="bold" textAlign="center" mb={4} color="primary.main">
        Generate Salary Slip
      </Typography>

      <Grid container spacing={3} justifyContent="center" alignItems="end">
        <Grid item xs={12} sm={5} md={4}>
          <Autocomplete
            options={employees}
            getOptionLabel={(o) => `${o.first_name} ${o.last_name} (${o.employee_code || o.id})`}
            onChange={(e, v) => setSlipForm({ ...slipForm, employee_id: v?.id || "" })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employee"
                variant="outlined"
                fullWidth
                sx={{ bgcolor: "white" }}
              />
            )}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            select
            fullWidth
            label="Month"
            value={slipForm.month}
            onChange={(e) => setSlipForm({ ...slipForm, month: e.target.value })}
            variant="outlined"
            sx={{ bgcolor: "white" }}
          >
            {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
              <MenuItem key={m} value={m}>
                {new Date(0, m - 1).toLocaleString("en", { month: "long" })}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            fullWidth
            label="Year"
            type="number"
            value={slipForm.year}
            onChange={(e) => setSlipForm({ ...slipForm, year: e.target.value })}
            variant="outlined"
            sx={{ bgcolor: "white" }}
          />
        </Grid>

        <Grid item xs={12} sm={3} md={2}>
          <LoadingButton
            loading={loading}
            variant="contained"
            size="large"
            fullWidth
            onClick={handleGenerateSlip}
            startIcon={<PictureAsPdf />}
            sx={{
              height: 56,
              bgcolor: "success.main",
              "&:hover": { bgcolor: "success.dark" },
              fontWeight: "bold",
            }}
            disabled={!slipForm.employee_id}
          >
            Generate Slip
          </LoadingButton>
        </Grid>
      </Grid>

      {/* Success Message with Animation */}
      {slipUrl && (
        <Box
          mt={5}
          p={4}
          borderRadius={3}
          textAlign="center"
          sx={{
            background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
            border: "2px solid #1976d2",
            animation: "fadeIn 0.8s ease-in",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(20px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Typography variant="h6" color="primary.main" fontWeight="bold" mb={3}>
            🎉 Pay Slip Generated Successfully!
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            href={slipUrl}
            target="_blank"
            rel="noopener"
            startIcon={<PictureAsPdf />}
            sx={{
              px: 6,
              py: 2,
              fontSize: "1.1rem",
              boxShadow: "0 8px 25px rgba(25, 118, 210, 0.3)",
              "&:hover": { boxShadow: "0 12px 35px rgba(25, 118, 210, 0.4)" },
            }}
          >
            Open / Download PDF
          </Button>
        </Box>
      )}
    </Paper>

    {/* Generated Slips List - Professional Table */}
    <Typography variant="h5" fontWeight="bold" mb={3} color="text.primary">
      Generated Salary Slips History
    </Typography>

    <EnhancedTable
      title=""
      data={availableSlips}
      columns={[
        { key: "employee_name", label: "Employee" },
        { key: "month", label: "Period", format: (_, r) => `${r.month}/${r.year}` },
        { key: "net_salary", label: "Net Salary", format: (v) => `₹${Number(v || 0).toLocaleString("en-IN")}` },
        { key: "created_at", label: "Generated On", format: v => v ? new Date(v).toLocaleDateString("en-IN") : "-" },
      ]}
      emptyText="No salary slips generated yet"
    />
  </>
)}
        {/* TAB 3: REPORT */}
        {tab === 3 && (
          <>
            <Box component={Paper} p={4} mb={4}>
              <Typography variant="h6" mb={3}>Monthly Salary Report</Typography>
              <Stack direction="row" spacing={2} alignItems="end">
                <TextField select label="Month" value={reportForm.month} onChange={e => setReportForm(prev => ({ ...prev, month: e.target.value }))}>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <MenuItem key={m} value={m}>{new Date(0, m-1).toLocaleString("en", {month:"long"})}</MenuItem>
                  ))}
                </TextField>
                <TextField label="Year" type="number" value={reportForm.year} onChange={e => setReportForm(prev => ({ ...prev, year: e.target.value }))} />
              </Stack>
            </Box>

            <EnhancedTable
              title={`Salary Report - ${new Date(0, reportForm.month - 1).toLocaleString("en", { month: "long" })} ${reportForm.year}`}
              data={salaryReport}
              columns={[
                { key: "employee_name", label: "Employee" },
                { key: "gross_salary", label: "Gross", format: v => `₹${Number(v||0).toLocaleString()}` },
                { key: "total_deduction", label: "Deductions", format: v => `₹${Number(v||0).toLocaleString()}` },
                { key: "net_salary", label: "Net Salary", format: v => `₹${Number(v||0).toLocaleString()}` },
                { key: "total_paid", label: "Paid", format: v => `₹${Number(v||0).toLocaleString()}` },
                { key: "remaining", label: "Remaining", format: v => <b style={{color: v>0 ? "red" : "green"}}>₹{Number(v||0).toLocaleString()}</b> },
              ]}
              emptyText="No data for selected month/year"
            />
          </>
        )}
      </Box>
    </HrLayout>
  );
}